import React, { useState } from 'react';
import { User, Package, Heart, Settings, LogOut, Edit, Check } from 'lucide-react';
import { Button, Card, Input } from '../components/common';

const AccountPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: 'John Doe',
    email: 'john@example.com',
    phone: '+263 77 123 4567',
    address: '123 Main Street, Harare, Zimbabwe',
  });

  const orders = [
    { id: 'ORD-001', date: '2024-01-15', total: '$45.99', status: 'Delivered' },
    { id: 'ORD-002', date: '2024-01-10', total: '$32.50', status: 'Processing' },
    { id: 'ORD-003', date: '2024-01-05', total: '$67.25', status: 'Delivered' },
  ];

  const wishlist = [
    { id: 1, name: 'Organic Tomatoes', price: '$4.99', image: 'https://images.unsplash.com/photo-1540420773420-3366772f4999' },
    { id: 2, name: 'Fresh Avocados', price: '$3.50', image: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578' },
    { id: 3, name: 'Sweet Corn', price: '$2.99', image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d' },
  ];

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'orders', label: 'My Orders', icon: Package },
    { id: 'wishlist', label: 'Wishlist', icon: Heart },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const handleSave = () => {
    setIsEditing(false);
    // Save profile data here
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                Personal Information
              </h3>
              {isEditing ? (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Check className="w-4 h-4" />}
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Edit className="w-4 h-4" />}
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </Button>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                disabled={!isEditing}
              />
              <Input
                label="Email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                disabled={!isEditing}
              />
              <Input
                label="Phone Number"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                disabled={!isEditing}
              />
              <Input
                label="Address"
                value={profileData.address}
                onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                disabled={!isEditing}
              />
            </div>
          </div>
        );

      case 'orders':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
              Order History
            </h3>
            
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white dark:text-dark-dark transition-colors duration-300">
                        Order {order.id}
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
                        Placed on {order.date}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-accent dark:text-primary transition-colors duration-300">
                        {order.total}
                      </p>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        order.status === 'Delivered'
                          ? 'bg-accent/20 dark:bg-primary/20 text-accent dark:text-primary'
                          : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-500'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'wishlist':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
              My Wishlist
            </h3>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {wishlist.map((item) => (
                <Card key={item.id} hover className="overflow-hidden">
                  <div className="aspect-square bg-dark-light dark:bg-gray-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-medium text-white dark:text-dark-dark mb-1 transition-colors duration-300">
                      {item.name}
                    </h4>
                    <p className="text-accent dark:text-primary font-bold mb-3 transition-colors duration-300">
                      {item.price}
                    </p>
                    <Button variant="primary" size="sm" className="w-full">
                      Add to Cart
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
              Account Settings
            </h3>
            
            <div className="space-y-4">
              <Card className="p-4">
                <h4 className="font-medium text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                  Notification Preferences
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded text-accent dark:text-primary" defaultChecked />
                    <span className="text-gray-300 dark:text-gray-600">Order updates</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded text-accent dark:text-primary" defaultChecked />
                    <span className="text-gray-300 dark:text-gray-600">New products</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="checkbox" className="rounded text-accent dark:text-primary" />
                    <span className="text-gray-300 dark:text-gray-600">Promotional offers</span>
                  </label>
                </div>
              </Card>
              
              <Card className="p-4">
                <h4 className="font-medium text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                  Security
                </h4>
                <Button variant="outline" size="sm" className="mb-3">
                  Change Password
                </Button>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Last password change: 30 days ago
                </p>
              </Card>
              
              <Card className="p-4 border-red-200 dark:border-red-300">
                <h4 className="font-medium text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                  Danger Zone
                </h4>
                <Button variant="danger" size="sm">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white dark:text-dark-dark transition-colors duration-300">
              My Account
            </h1>
            <p className="text-gray-400 dark:text-gray-500 transition-colors duration-300">
              Manage your profile, orders, and preferences
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium text-white dark:text-dark-dark transition-colors duration-300">
                {profileData.name}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
                Member since 2023
              </p>
            </div>
            <div className="w-12 h-12 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-accent dark:text-primary" />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 w-full p-3 rounded-lg transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-accent/20 dark:bg-primary/20 text-accent dark:text-primary'
                      : 'text-gray-300 dark:text-gray-600 hover:bg-dark-light dark:hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </Card>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <Card className="p-6">
              {renderContent()}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;